import { useEffect } from 'react'
import { router, useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { FdeEditorDialog } from '@/frontend/features/Fde/FdeEditorSurface'
import { TextControl } from '@/frontend/features/Fde/FormControls'
import ParticipantProfileFields from '@/frontend/features/Fde/ParticipantProfileFields'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { FdeCopy, ProjectAccessMember } from '@/frontend/features/Fde/types'

interface Props {
  open: boolean; onOpenChange: (open: boolean) => void; projectId: string
  member: ProjectAccessMember | null; copy: FdeCopy
}

export default function ProjectAccessDialog({ open, onOpenChange, projectId, member, copy }: Props) {
  const form = useForm({ role_name: '', job_title: '', description: '' })
  useEffect(() => {
    if (!open || !member) return
    form.setData({ role_name: member.role_name, job_title: member.job_title || '', description: member.description || '' })
    form.clearErrors()
  }, [open, member?.id])
  if (!member) return null
  const memberPath = fdeProjectPath(`${fdeRoutes.projectMembers}/${member.id}`, projectId)

  function submit() {
    form.transform((data) => ({ project_member: data, return_to: 'access' }))
    form.patch(memberPath, { preserveScroll: true, onSuccess: () => onOpenChange(false) })
  }
  function remove() {
    if (!window.confirm(copy.management.confirm_remove_access)) return
    router.delete(memberPath, { data: { return_to: 'access' }, preserveScroll: true, onSuccess: () => onOpenChange(false) })
  }

  return <FdeEditorDialog open={open} onOpenChange={onOpenChange} title={member.name} closeLabel={copy.common.close} size="standard" footer={<>
    <Button variant="destructive" className="sm:mr-auto" onClick={remove}>{copy.management.remove_access}</Button>
    <Button variant="outline" onClick={() => onOpenChange(false)}>{copy.common.cancel}</Button>
    <Button disabled={form.processing || !form.data.role_name.trim()} onClick={submit}>{copy.common.save}</Button>
  </>}>
    <TextControl label={copy.people.project_role} value={form.data.role_name} onChange={(event) => form.setData('role_name', event.target.value)} required />
    <ParticipantProfileFields copy={copy} jobTitle={form.data.job_title} description={form.data.description} onTitleChange={(value) => form.setData('job_title', value)} onDescriptionChange={(value) => form.setData('description', value)} />
    {Object.values(form.errors)[0] && <p role="alert" className="text-sm text-destructive">{Object.values(form.errors)[0]}</p>}
  </FdeEditorDialog>
}
